const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: Fab } = require('@mui/material/Fab');
const { default: UploadIcon } = require('@mui/icons-material/Publish');
const { default: UploadedFileIcon } = require('@mui/icons-material/Attachment');
const { default: Typography } = require('@mui/material/Typography');

const { useRef } = React;

const internals = {};

module.exports = function CsvUploadField(props) {

    const {
        accept = 'text/csv',
        contextLabel,
        description,
        disabled = false,
        emptyLabel = 'CSV file required',
        error,
        fabInputRef,
        helperText,
        inputProps,
        minHeight = 112,
        onBlur,
        onChange,
        onFocus,
        showActionButton = false,
        value
    } = props;

    const internalInputRef = useRef(null);
    const inputRef = fabInputRef || internalInputRef;
    const { Wrapper, Instructions, UploadShell, UploadArea, UploadTrigger, UploadedFile, UploadFab, HiddenInput } = internals;

    const selectFile = (event) => {

        const nextFile = event.target.files?.[0] || null;

        if (!nextFile) {
            return;
        }

        onChange?.(nextFile, event);
        event.target.value = null;
    };

    const openPicker = () => {

        if (disabled) {
            return;
        }

        inputRef.current?.click();
    };

    return (
        <Wrapper>
            {!!description && <Instructions>{description}</Instructions>}
            {!!contextLabel && <Instructions>{contextLabel}</Instructions>}
            <UploadShell $showActionButton={showActionButton}>
                <UploadArea
                    $disabled={disabled}
                    $minHeight={minHeight}
                    onClick={openPicker}
                    role='button'
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={(event) => {

                        if (disabled) {
                            return;
                        }

                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openPicker();
                        }
                    }}
                >
                    {value?.name ?
                        <UploadedFile>
                            <UploadedFileIcon />
                            <span>{value.name}</span>
                        </UploadedFile> :
                        <UploadTrigger>{emptyLabel}</UploadTrigger>}
                </UploadArea>
                {showActionButton && <UploadFab
                    color='primary'
                    aria-label='upload csv file'
                    onClick={openPicker}
                    disabled={disabled}
                >
                    <UploadIcon style={{ fill: '#ffffff' }} />
                </UploadFab>}
                <HiddenInput
                    ref={inputRef}
                    disabled={disabled}
                    type='file'
                    accept={accept}
                    onBlur={onBlur}
                    onChange={selectFile}
                    onFocus={onFocus}
                    {...inputProps}
                />
            </UploadShell>
            {!!helperText && <Typography color={error ? 'error.main' : 'text.secondary'}>{helperText}</Typography>}
        </Wrapper>
    );
};

module.exports.propTypes = {
    accept: T.string,
    contextLabel: T.node,
    description: T.node,
    disabled: T.bool,
    emptyLabel: T.string,
    error: T.oneOfType([T.bool, T.object]),
    fabInputRef: T.shape({
        current: T.any
    }),
    helperText: T.string,
    inputProps: T.object,
    minHeight: T.number,
    onBlur: T.func,
    onChange: T.func,
    onFocus: T.func,
    showActionButton: T.bool,
    value: T.any
};

internals.Wrapper = Styled.div`
    width: 100%;
`;

internals.Instructions = Styled.div`
    margin-top: 16px;
`;

internals.UploadShell = Styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    width: 100%;

    ${({ $showActionButton }) => !$showActionButton ? `
        gap: 0;
    ` : ''}

    @media (max-width: 599px) {
        align-items: stretch;
        flex-direction: column;
    }
`;

internals.UploadArea = Styled.div`
    background: #af004c;
    color: #ffffff;
    cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: ${({ $minHeight }) => `${$minHeight}px`};
    opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
    padding: 16px;
    position: relative;
    text-align: center;
    width: 100%;

    &::before {
        content: '';
        position: absolute;
        inset: 10px;
        border: 2px dashed #d8d8d8;
        pointer-events: none;
    }
`;

internals.UploadTrigger = Styled.span`
    text-decoration: underline;
`;

internals.UploadedFile = Styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: center;
    overflow-wrap: anywhere;
`;

internals.UploadFab = Styled(Fab)`
    flex: 0 0 auto;

    @media (max-width: 599px) {
        align-self: flex-end;
    }
`;

internals.HiddenInput = Styled.input`
    height: 0;
    visibility: hidden;
    width: 0;
`;

module.exports.internals = internals;
